import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { AppointmentScheduler } from './AppointmentScheduler';
export function Hero() {
  const [formData, setFormData] = useState({
    interest: 'myself',
    fullName: '',
    phone: '',
    email: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const params = new URLSearchParams();
      params.append('interest', formData.interest);
      params.append('full_name', formData.fullName);
      params.append('phone', formData.phone);
      params.append('email', formData.email);
      await fetch('https://services.leadconnectorhq.com/hooks/9bw9Tk4GstO34KNKeVbp/webhook-trigger/8b1640ae-2b70-46f8-92d5-f359f32a0fa6', {
        method: 'POST',
        body: params
      });
      setStatus('success');
    } catch {
      // Still show success to the user — the lead will be retried or handled manually
      setStatus('success');
    }
  };
  return <section className="relative bg-brand-dark pt-6 pb-10 lg:pt-32 lg:pb-32 overflow-hidden" data-id="element-109">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-110">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-12 items-center" data-id="element-111">
          {/* Headline Block */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="text-center lg:text-left order-1 lg:order-1 max-w-2xl mx-auto lg:mx-0" data-id="element-112">
            <p className="text-brand-muted text-xs lg:text-sm font-medium uppercase tracking-widest mb-2 lg:mb-8" data-id="element-113">
              Miami Lakes • Hialeah
            </p>

            <h1 className="text-2xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-3 lg:mb-6 tracking-tight" data-id="element-114">
              <span className="lg:hidden" data-id="element-115">Join the Lima Dental VIP Plan</span>
              <span className="hidden lg:inline" data-id="element-116">
                Join the Lima Dental VIP Plan for{' '}
                <span className="text-white font-medium" data-id="element-117">$500/year</span>
              </span>
            </h1>

            {/* Subtitle & paragraph — visible only on desktop */}
            <div className="hidden lg:block" data-id="element-118">
              <p className="text-xl text-white/90 font-light mb-6" data-id="element-119">
                Affordable, family-friendly dental care from a trusted Miami
                Lakes practice.
              </p>
              <p className="text-lg text-brand-muted mb-8 leading-relaxed font-light" data-id="element-120">
                No insurance? No problem. Get the preventive care you need—plus
                savings on additional treatments—without the hassle of premiums
                or surprise bills.
              </p>
            </div>
          </motion.div>

          {/* Form Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} className="relative order-2 lg:order-2" id="lead-form" data-id="element-121">
            <div className="bg-white rounded-2xl lg:rounded-3xl p-5 sm:p-10 relative z-10 shadow-2xl shadow-black/30" data-id="element-122">
              {status === 'success' ? <AppointmentScheduler formData={formData} data-id="element-123" /> : <>
                  <div className="text-center mb-5 lg:mb-8" data-id="element-124">
                    <p className="text-brand-muted text-xs font-medium uppercase tracking-widest mb-1" data-id="element-125">
                      Annual Membership
                    </p>
                    <p className="text-3xl lg:text-4xl font-light text-brand-dark tracking-tight" data-id="element-126">
                      $500
                      <span className="text-lg lg:text-xl text-brand-muted ml-1" data-id="element-127">
                        /year
                      </span>
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6" data-id="element-128">
                    <div data-id="element-129">
                      <label className="block text-sm font-medium text-brand-dark/80 mb-3" data-id="element-130">
                        Is this for yourself or your family?
                      </label>
                      <div className="grid grid-cols-2 gap-3 lg:gap-4" data-id="element-131">
                        <label className={`border rounded-xl p-3 lg:p-4 flex items-center cursor-pointer transition-colors ${formData.interest === 'myself' ? 'border-brand-dark bg-brand-dark/5' : 'border-slate-200 hover:border-brand-dark/40'}`} data-id="element-132">
                          <input type="radio" name="interest" value="myself" checked={formData.interest === 'myself'} onChange={e => setFormData({
                        ...formData,
                        interest: e.target.value
                      })} className="text-brand-dark focus:ring-brand-dark h-4 w-4" data-id="element-133" />
                          <span className="ml-3 text-sm font-medium text-brand-dark" data-id="element-134">
                            Just Myself
                          </span>
                        </label>
                        <label className={`border rounded-xl p-3 lg:p-4 flex items-center cursor-pointer transition-colors ${formData.interest === 'family' ? 'border-brand-dark bg-brand-dark/5' : 'border-slate-200 hover:border-brand-dark/40'}`} data-id="element-135">
                          <input type="radio" name="interest" value="family" checked={formData.interest === 'family'} onChange={e => setFormData({
                        ...formData,
                        interest: e.target.value
                      })} className="text-brand-dark focus:ring-brand-dark h-4 w-4" data-id="element-136" />
                          <span className="ml-3 text-sm font-medium text-brand-dark" data-id="element-137">
                            My Family
                          </span>
                        </label>
                      </div>
                    </div>

                    <div data-id="element-138">
                      <label htmlFor="fullName" className="hidden lg:block text-sm font-medium text-brand-dark/80 mb-2" data-id="element-139">
                        Full Name
                      </label>
                      <input type="text" id="fullName" required className="w-full px-3 lg:px-4 py-3 lg:py-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-1 focus:ring-brand-dark focus:border-brand-dark outline-none transition-all text-brand-dark placeholder-slate-400 font-light" placeholder="Full Name" value={formData.fullName} onChange={e => setFormData({
                    ...formData,
                    fullName: e.target.value
                  })} data-id="element-140" />
                    </div>

                    <div data-id="element-141">
                      <label htmlFor="phone" className="hidden lg:block text-sm font-medium text-brand-dark/80 mb-2" data-id="element-142">
                        Phone Number
                      </label>
                      <input type="tel" id="phone" required className="w-full px-3 lg:px-4 py-3 lg:py-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-1 focus:ring-brand-dark focus:border-brand-dark outline-none transition-all text-brand-dark placeholder-slate-400 font-light" placeholder="Phone Number" inputMode="numeric" pattern="[0-9]*" value={formData.phone} onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({
                      ...formData,
                      phone: val
                    });
                  }} data-id="element-143" />
                    </div>

                    <div data-id="element-144">
                      <label htmlFor="email" className="hidden lg:block text-sm font-medium text-brand-dark/80 mb-2" data-id="element-145">
                        Email Address
                      </label>
                      <input type="email" id="email" required className="w-full px-3 lg:px-4 py-3 lg:py-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-1 focus:ring-brand-dark focus:border-brand-dark outline-none transition-all text-brand-dark placeholder-slate-400 font-light" placeholder="Email Address" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} data-id="element-146" />
                    </div>

                    <button type="submit" disabled={status === 'submitting'} className="w-full bg-brand-dark hover:bg-brand-dark/90 text-white font-medium py-3 lg:py-4 px-6 rounded-full transition-all disabled:opacity-70 mt-2 lg:mt-4" data-id="element-147">
                      {status === 'submitting' ? 'Submitting...' : 'Join the VIP Plan'}
                    </button>
                    <p className="text-xs text-center text-brand-muted mt-4 font-light" data-id="element-148">
                      By submitting, you agree to be contacted about the VIP
                      Plan. No commitment required today.
                    </p>
                  </form>
                </>}
            </div>
          </motion.div>

          {/* Mobile subtitle — compact */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: 'easeOut'
        }} className="block lg:hidden order-3 text-center" data-id="element-149">
            <p className="text-base lg:text-xl text-white/90 font-light mb-2 lg:mb-4" data-id="element-150">
              Affordable, family-friendly dental care from a trusted Miami Lakes
              practice.
            </p>
            <p className="text-sm lg:text-lg text-brand-muted leading-relaxed font-light" data-id="element-151">
              No insurance? No problem. Get the preventive care you need—plus
              savings on additional treatments—without the hassle of premiums or
              surprise bills.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}